import { Colors } from '@/common/colors/colors';
import Input from '@/common/components/Input';
import PopUp from '@/common/components/PopUp';
import React, { useRef, useState, useEffect } from 'react';
import {
	ButtonWrapper,
	CorrectionReasonsContainer,
	CorrectionReasonsLabel,
	ReasonRow,
} from '../../InvoiceConfig.styled';
import { preventFn } from '@/common/functions/preventEvent';
import { useRequestsContext } from '@/common/hooks/requestHook';
import Loading from '@/common/components/Loading';
import {
	createInvoiceConfig,
	updateInvoiceConfig,
} from '@/API/repositories/invoiceConfig';
import { useMessageQueueContext } from '@/common/hooks/useMessageQueue';
import SubmitButton from '@/common/components/buttons/SubmitButton';
import Icon from '@/common/components/Icon';
import { Button } from 'react-bootstrap';

const InvoiceConfigForm = ({ setShow, loadData, selectedInvoiceConfig }) => {
	const [reasons, setReasons] = useState(['']);

	const buyerNoteRef = useRef();
	const descriptionRef = useRef();
	const nameRef = useRef();
	const taxExemptRef = useRef();

	const { makeRequest, hasUnfilledRequest } = useRequestsContext();
	const { addMessage } = useMessageQueueContext();

	useEffect(() => {
		if (selectedInvoiceConfig?.correction_reasons) {
			setReasons(selectedInvoiceConfig.correction_reasons);
		}
	}, [selectedInvoiceConfig]);

	const handleReasonChange = (index, value) => {
		const newReasons = [...reasons];
		newReasons[index] = value;
		setReasons(newReasons);
	};

	const addReasonField = () => setReasons([...reasons, '']);

	const removeReasonField = (index) => {
		setReasons((prevReasons) => {
			if (prevReasons.length <= 1) return prevReasons;

			return prevReasons.filter((_, i) => i !== index);
		});
	};

	const handleCreateKey = async (e) => {
		e && preventFn(e);

		const payload = {};

		payload.buyer_note = buyerNoteRef.current.value;
		payload.description = descriptionRef.current.value;
		payload._id = selectedInvoiceConfig?._id;
		payload.name = nameRef.current.value;
		payload.exempt_tax_kind = taxExemptRef.current.value;
		payload.correction_reasons = reasons;

		const response = selectedInvoiceConfig
			? await makeRequest(updateInvoiceConfig.bind(null, payload))
			: await makeRequest(createInvoiceConfig.bind(null, payload));

		if (!response?.data) {
			return addMessage('Something went wrong', 'error');
		}

		setShow(() => false);
		addMessage('Success', 'success');
		loadData();
	};

	return (
		<PopUp padding='10px 20px 20px' setShow={setShow}>
			{hasUnfilledRequest(updateInvoiceConfig, createInvoiceConfig) && (
				<Loading />
			)}
			<form onSubmit={(e) => handleCreateKey(e)}>
				<Input
					inputRef={nameRef}
					required
					color={Colors.darkBlue}
					width={100}
					inputWidth={450}
					name='Name'
					value={selectedInvoiceConfig?.name}
					placeholder='e.g CZ_INVOICE_CONFIG'
				/>
				<Input
					inputRef={buyerNoteRef}
					required
					color={Colors.darkBlue}
					width={100}
					inputWidth={450}
					name='Buyer note'
					value={selectedInvoiceConfig?.buyer_note}
					placeholder='e.g IČO'
				/>
				<CorrectionReasonsContainer>
					<CorrectionReasonsLabel>Correction Reasons</CorrectionReasonsLabel>
					{reasons.map((reason, index) => (
						<ReasonRow key={`reason-${index}-${reason}`}>
							<Input
								value={reason}
								onChange={(e) => handleReasonChange(index, e.target.value)}
								placeholder='e.g Order cancelation / <other_language>'
								width={0}
								inputWidth={410}
								required
							/>
							{reasons.length > 1 && (
								<Icon
									name='fa fa-trash'
									onClick={() => {
										removeReasonField(index);
									}}
									style={{ color: Colors.red, cursor: 'pointer' }}
								/>
							)}
						</ReasonRow>
					))}
					<Button
						type='button'
						onClick={addReasonField}
						style={{ marginTop: '5px', cursor: 'pointer' }}
					>
						+ Add Reason
					</Button>
				</CorrectionReasonsContainer>
				<Input
					inputRef={descriptionRef}
					required
					name='Description'
					width={100}
					inputWidth={450}
					placeholder='e.g Reverse charge / Prenos daňovej povinnosti'
					color={Colors.darkBlue}
					value={selectedInvoiceConfig?.description}
				/>
				<Input
					inputRef={taxExemptRef}
					name='Tax exempt'
					width={105}
					inputWidth={450}
					required
					color={Colors.darkBlue}
					value={selectedInvoiceConfig?.exempt_tax_kind}
				/>
				<ButtonWrapper>
					<SubmitButton
						color={Colors.orange}
						text={`${
							selectedInvoiceConfig ? 'Update' : 'Create'
						} invoice config`}
					/>
				</ButtonWrapper>
			</form>
		</PopUp>
	);
};

export default InvoiceConfigForm;
